import React, { useState, createContext, useMemo, useContext, useEffect, useRef } from 'react';
import socketIO from 'socket.io-client';
import { $BASE_URL } from "src/constants";
import AuthService from 'src/_services/AuthService';

const SocketContext = createContext()

const SocketContextProvider = ({ currentUser, children }) => {

    const [socket, setSocket] = useState(null);
    const socketRef = useRef(null);

    // `currentUser` is a fresh object on every getCurrentUserProfile() dispatch, so the
    // effect must key on the stable id — otherwise a profile refetch tears down the socket.
    const userId = currentUser?.merchant?.id ?? currentUser?.id;

    useEffect(_ => {
        const jwt = AuthService.getJwt()

        if (!userId || !jwt) {
            if (socketRef.current) {
                socketRef.current.disconnect()
                socketRef.current = null
                setSocket(null)
            }
            return;
        }

        if (socketRef.current) return;

        const s = socketIO(
            $BASE_URL,
            {
                transports: ['websocket'],
                jsonp: false,
                auth: {
                    token: `Bearer ${jwt}`
                },
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 10000,
            }
        );

        s.on('connect', () => {
            console.log(`Connected to socket server.`);
            s.emit('register', { userId })
        });

        s.on('disconnect', (reason) => {
            console.log(`Disconnected from socket server.`, reason);
        });

        s.on("connect_error", (error) => {
            console.log(error)
        });

        socketRef.current = s;
        setSocket(s);

        return _ => {
            s.disconnect()
            if (socketRef.current === s) {
                socketRef.current = null
                setSocket(null)
            }
        }
    }, [userId])

    const value = useMemo(() => ({
        socket
    }), [socket])

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    )
}

export const useSocket = () => useContext(SocketContext);

export default SocketContextProvider;
